import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { sliderBottom } from "d3-simple-slider";
import Loading from "../../../common/components/loading/loading";
import {
  getTimeAxisTicks,
  getLinearAxisTickCount,
} from "../../../common/components/d3/d3ChartTickUtilities";
import {
  appendSignedVerticalAreaGradients,
  buildTooltipHtml,
  createZeroAnchoredArea,
  createTooltipHandlers,
  formatTooltipCurrency,
  formatTooltipDate,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../../common/components/d3/d3ChartShared";

const ForwardCurveGraph = ({
  data,
  title,
  yAxisLabel,
  isLoading,
  initialStartDate,
  initialEndDate,
}) => {
  const svgRef = useRef();
  const sliderRef = useRef();
  const tooltipRef = useRef(null);

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(function () {
          if (Date.now() - lastRan >= limit) {
            func.apply(context, args);
            lastRan = Date.now();
          }
        }, Math.max(0, limit - (Date.now() - lastRan)));
      }
    };
  };

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);
    if (!svg.node()) return;

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight - 20;
    const margin = { top: 30, right: 10, bottom: 60, left: 60 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const processed = (Array.isArray(data)
      ? data.filter(
          (d) =>
            d &&
            (d.beginDate || d.endDate) &&
            d.settlePrice !== null &&
            d.settlePrice !== undefined,
        )
      : []
    )
      .map((d) => {
        const beginDate = d.beginDate ? new Date(d.beginDate) : null;
        const endDate = d.endDate ? new Date(d.endDate) : null;

        let xDate;
        if (beginDate && !isNaN(beginDate)) {
          if (endDate && !isNaN(endDate)) {
            xDate = new Date((beginDate.getTime() + endDate.getTime()) / 2);
          } else {
            xDate = new Date(
              beginDate.getFullYear(),
              beginDate.getMonth(),
              beginDate.getDate(),
            );
          }
        } else if (endDate && !isNaN(endDate)) {
          xDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        } else {
          xDate = null;
        }

        return {
          ...d,
          settlePrice: Number(d.settlePrice),
          xDate,
        };
      })
      .filter((d) => d.xDate && !isNaN(d.settlePrice))
      .sort((a, b) => a.xDate - b.xDate);

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "d3-chart-title");

    if (!processed.length) {
      chartSvg
        .append("text")
        .attr("x", innerWidth / 2)
        .attr("y", innerHeight / 2)
        .attr("text-anchor", "middle")
        .text("No data")
        .attr("class", "d3-chart-legend");
      return;
    }

    const highlightColor = "#5f9fcf";

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(processed, (d) => d.xDate))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain(getNumericExtentIncludingZero(processed.map((d) => d.settlePrice)))
      .nice()
      .range([innerHeight, 0]);

    let tooltipData = processed;

    const sortedDates = processed.map((d) => d.xDate).sort((a, b) => a - b);
    const xTickValues = getTimeAxisTicks(sortedDates, innerWidth, 80);

    const xAxisG = chartSvg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(xTickValues.length > 0 ? xTickValues : undefined)
          .ticks(
            xTickValues.length > 0
              ? undefined
              : Math.min(8, Math.ceil(innerWidth / 80)),
          )
          .tickFormat(d3.timeFormat("%Y-%m-%d")),
      );

    const maxYTicks = getLinearAxisTickCount(innerHeight, 40);
    const yAxisG = chartSvg
      .append("g")
      .attr("class", "y-axis")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(maxYTicks)
          .tickFormat((v) => `$${v.toFixed(2)}`),
      );

    xAxisG.selectAll("path,line").attr("stroke", "grey");
    xAxisG.selectAll("text").attr("fill", "grey");
    yAxisG.selectAll("path,line").attr("stroke", "grey");
    yAxisG.selectAll("text").attr("fill", "grey");

    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text(yAxisLabel)
      .attr("class", "d3-chart-axis-label");

    const gridlines = chartSvg
      .append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""))
      .attr("stroke-opacity", 0.1);

    const gradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "forward-curve-area",
      color: highlightColor,
    });

    const line = d3
      .line()
      .defined((d) => d && d.xDate && !isNaN(d.settlePrice))
      .x((d) => xScale(d.xDate))
      .y((d) => yScale(d.settlePrice))
      .curve(d3.curveMonotoneX);

    const positiveArea = createZeroAnchoredArea({
      valueAccessor: (d) => d.settlePrice,
      xAccessor: (d) => d.xDate,
      xScale,
      yScale,
    });
    const negativeArea = createZeroAnchoredArea({
      polarity: "negative",
      valueAccessor: (d) => d.settlePrice,
      xAccessor: (d) => d.xDate,
      xScale,
      yScale,
    });

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const negativeAreaPath = areaLayer
      .append("path")
      .datum(processed)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradients.negativeGradientId})`)
      .attr("d", negativeArea);
    const positiveAreaPath = areaLayer
      .append("path")
      .datum(processed)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradients.positiveGradientId})`)
      .attr("d", positiveArea);

    const path = chartSvg
      .append("path")
      .datum(processed)
      .attr("class", "settlement-line")
      .attr("fill", "none")
      .attr("stroke", highlightColor)
      .attr("stroke-width", 2)
      .attr("d", line);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    const tooltipBisector = d3.bisector((d) => d.xDate).center;
    const getNearestTooltipDatum = (event) => {
      if (!tooltipData.length) {
        return null;
      }

      const [pointerX] = d3.pointer(event, chartSvg.node());
      const hoveredValue = xScale.invert(pointerX);
      const index = tooltipBisector(tooltipData, hoveredValue);
      return tooltipData[index] ?? null;
    };

    chartSvg
      .append("rect")
      .attr("class", "d3-chart-hover-overlay")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("fill", "#ffffff")
      .attr("fill-opacity", 0.001)
      .style("pointer-events", "all")
      .raise()
      .on("mouseover", (event) => {
        const datum = getNearestTooltipDatum(event);
        if (!datum) {
          hideTooltip();
          return;
        }

        showTooltip(
          event,
          buildTooltipHtml({
            title: formatTooltipDate(datum.xDate, "%Y-%m-%d"),
            rows: [
              {
                label: yAxisLabel,
                value: formatTooltipCurrency(datum.settlePrice, 2),
                color: highlightColor,
              },
            ],
          }),
        );
      })
      .on("mousemove", (event) => {
        const datum = getNearestTooltipDatum(event);
        if (!datum) {
          hideTooltip();
          return;
        }

        showTooltip(
          event,
          buildTooltipHtml({
            title: formatTooltipDate(datum.xDate, "%Y-%m-%d"),
            rows: [
              {
                label: yAxisLabel,
                value: formatTooltipCurrency(datum.settlePrice, 2),
                color: highlightColor,
              },
            ],
          }),
        );
      })
      .on("mouseout", hideTooltip);

    const sliderSvg = d3
      .select(sliderRef.current)
      .attr("width", parentWidth)
      .attr("height", "40px")
      .attr("preserveAspectRatio", "none")
      .attr("class", "no-print")
      .attr("transform", "translate(0,-20)");

    sliderSvg.selectAll("*").remove();
    const sliderWidth = innerWidth;

    const updateChart = (range) => {
      const [startDate, endDate] = range.map((d) =>
        d instanceof Date ? d : new Date(d),
      );

      const filtered = processed.filter(
        (d) => d.xDate >= startDate && d.xDate <= endDate,
      );
      tooltipData = filtered;

      xScale.domain([startDate, endDate]);
      yScale
        .domain(getNumericExtentIncludingZero(filtered.map((d) => d.settlePrice)))
        .nice();

      const t = d3.transition().duration(200).ease(d3.easeLinear);
      const filteredDates = filtered.map((d) => d.xDate).sort((a, b) => a - b);
      const updatedXTickValues = getTimeAxisTicks(filteredDates, innerWidth, 80);
      const updatedYTicks = getLinearAxisTickCount(innerHeight, 40);

      xAxisG.transition(t).call(
        d3
          .axisBottom(xScale)
          .tickValues(
            updatedXTickValues.length > 0 ? updatedXTickValues : undefined,
          )
          .ticks(
            updatedXTickValues.length > 0
              ? undefined
              : Math.min(8, Math.ceil(innerWidth / 80)),
          )
          .tickFormat(d3.timeFormat("%Y-%m-%d")),
      );
      yAxisG.transition(t).call(
        d3
          .axisLeft(yScale)
          .ticks(updatedYTicks)
          .tickFormat((v) => `$${v.toFixed(2)}`),
      );
      gridlines
        .transition(t)
        .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

      negativeAreaPath.datum(filtered).transition(t).attr("d", negativeArea);
      positiveAreaPath.datum(filtered).transition(t).attr("d", positiveArea);
      path.datum(filtered).transition(t).attr("d", line);
    };

    const throttledUpdateChart = throttle(updateChart, 30);

    const slider = sliderBottom()
      .min(d3.min(processed, (d) => d.xDate))
      .max(d3.max(processed, (d) => d.xDate))
      .width(sliderWidth)
      .tickFormat(d3.timeFormat("%Y-%m-%d"))
      .ticks(5)
      .default([
        initialStartDate
          ? new Date(initialStartDate)
          : d3.min(processed, (d) => d.xDate),
        initialEndDate
          ? new Date(initialEndDate)
          : d3.max(processed, (d) => d.xDate),
      ])
      .fill("var(--theme-interactive-element)")
      .on("onchange", throttledUpdateChart);

    sliderSvg
      .append("g")
      .attr("transform", `translate(${(parentWidth - sliderWidth) / 2},0)`)
      .call(slider);

    sliderSvg.selectAll(".slider text").style("fill", "grey");
    sliderSvg.selectAll(".tick text").style("opacity", "0");

    updateChart([
      initialStartDate
        ? new Date(initialStartDate)
        : d3.min(processed, (d) => d.xDate),
      initialEndDate
        ? new Date(initialEndDate)
        : d3.max(processed, (d) => d.xDate),
    ]);
  }, [data, title, initialStartDate, initialEndDate, yAxisLabel]);

  useEffect(() => {
    if (!isLoading) {
      renderChart();
    }
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);
    observer.observe(svgNode.parentNode);

    return () => {
      try {
        observer.disconnect();
      } catch (e) {
        // ignore
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    return () => {
      removeTooltip(tooltipRef);
    };
  }, []);

  if (isLoading ?? true) return <Loading />;

  return (
    <div className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} style={{ width: "100%" }}></svg>
      <svg ref={sliderRef} style={{ width: "100%" }}></svg>
    </div>
  );
};

export default ForwardCurveGraph;
